import { useEffect, useRef, useState } from "react";
import { api, API_URL, fmtDate, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Download, Trash2, FileText, Image as ImageIcon, FileSpreadsheet, File as FileIcon, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["General", "Brochure", "Drawing", "Client Document", "Certificate", "Media", "Other"];

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const TypeIcon = ({ ct }) => {
  if ((ct || "").startsWith("image/")) return <ImageIcon size={16} className="text-violet-500" />;
  if ((ct || "").includes("pdf")) return <FileText size={16} className="text-rose-500" />;
  if ((ct || "").includes("sheet") || (ct || "").includes("excel") || (ct || "").includes("csv")) return <FileSpreadsheet size={16} className="text-emerald-600" />;
  return <FileIcon size={16} className="text-slate-400" />;
};

export default function FilesMedia() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [uploadCat, setUploadCat] = useState("General");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = () => api.get("/files").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", uploadCat);
        await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`Uploaded ${f.name}`);
      } catch (err) { toast.error(`${f.name}: ${formatApiError(err)}`); }
    }
    setBusy(false);
    load();
  };

  const del = async (f) => {
    if (!window.confirm(`Delete "${f.original_filename}"?`)) return;
    try {
      await api.delete(`/files/${f.id}`);
      toast.success("File deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = list.filter((f) =>
    (cat === "All" || f.category === cat) &&
    (!q || (f.original_filename || "").toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-4" data-testid="files-page">
      <PageHeader title="Files & Media" subtitle="Company documents, brochures, drawings & media — stored in the cloud"
        right={
          <div className="flex items-center gap-2">
            <Select value={uploadCat} onValueChange={setUploadCat}>
              <SelectTrigger className="h-9 w-40 text-xs" data-testid="files-upload-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <input ref={inputRef} type="file" multiple className="hidden" onChange={onPick} data-testid="files-input" />
            <Button onClick={() => inputRef.current?.click()} disabled={busy}
                    className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="files-upload-btn">
              {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />} Upload
            </Button>
          </div>
        } />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search files…"
                 className="pl-8 h-9 w-64" data-testid="files-search" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="h-9 w-44 text-xs" data-testid="files-filter-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All categories</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500 ml-auto">{filtered.length} file{filtered.length === 1 ? "" : "s"}</span>
      </div>

      <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">File</th>
              <th className="text-left px-4 py-2.5 hidden md:table-cell">Category</th>
              <th className="text-right px-4 py-2.5 hidden md:table-cell">Size</th>
              <th className="text-left px-4 py-2.5 hidden lg:table-cell">Uploaded by</th>
              <th className="text-left px-4 py-2.5">Date</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((f) => (
              <tr key={f.id} className="border-t border-slate-100 hover:bg-slate-50/70">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <TypeIcon ct={f.content_type} />
                    <span className="font-medium text-slate-800 truncate max-w-[320px]">{f.original_filename}</span>
                  </div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{f.category}</span>
                </td>
                <td className="px-4 py-3 text-right tabular-nums hidden md:table-cell text-slate-600">{fmtSize(f.size)}</td>
                <td className="px-4 py-3 hidden lg:table-cell text-slate-600">{f.uploaded_by || "-"}</td>
                <td className="px-4 py-3 text-slate-600">{fmtDate(f.created_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <a href={`${API_URL}/files/${f.id}/download`} target="_blank" rel="noreferrer"
                     className="inline-flex p-1.5 text-slate-400 hover:text-blue-600 transition-colors duration-200" title="View / download" data-testid={`file-download-${f.id}`}>
                    <Download size={14} />
                  </a>
                  <button onClick={() => del(f)} className="p-1.5 text-slate-400 hover:text-rose-600 transition-colors duration-200" title="Delete" data-testid={`file-delete-${f.id}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-14 text-sm text-slate-500">
                No files yet — click <span className="font-semibold">Upload</span> to add brochures, drawings, client documents or media.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
